import { createSharedComposable } from '@vueuse/core';

// eslint-disable-next-line regex/invalid
import { SdpLeadModal } from '#components';
import { type BackendErrorEntity, type ErrorType, LeadIntent, type PostLeadBodyDto, SellingMethod, useApiPostLead } from '~/apiClient';
import type { LeadModalProps } from '~/components/Sdp/SdpLeadModal.vue';
import type { PlatformSaleItemEntity } from '~/types/sale.type';

type UseLeadModalReturn = {
    captureLeadContacts: (params: LeadModalProps) => Promise<CustomerContacts | undefined>;
    closeLeadModal: VoidFunction;
};

export const _useSdpLead = (): UseLeadModalReturn => {
    // COMPOSABLES
    const { data: session, isLoggedIn } = useAuthUtils();
    const { mutateAsync: mutateSubmit } = useApiPostLead();
    const { locale } = useTypedI18n();
    const route = useRoute();
    const { logError } = useLogs();
    const { getCached, setCached } = useCustomerContactsCache();
    const overlay = useOverlay();
    const leadModal = overlay.create(SdpLeadModal);

    // METHODS
    const postLead = async (contacts: CustomerContacts, sale: PlatformSaleItemEntity, intentType: LeadIntent): Promise<void> => {
        const body: PostLeadBodyDto = {
            ...{ [sale.sellingMethod === SellingMethod.Auction ? 'auctionId' : 'directSaleId']: sale.id },
            ...contacts,
            intentType,
            language: localeToLanguage(locale.value),
            urlPath: route.fullPath,
        };

        await mutateSubmit({
            data: body,
        }).catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error posting user lead Data', { userId: session.value?.user, body, apiError });
        });
    };

    const captureLeadContacts: UseLeadModalReturn['captureLeadContacts'] = async ({ sale, intentType }) => {
        const { phone, email } = session.value?.user ?? {};

        if (isLoggedIn.value && phone && email) {
            await postLead({ email, phone }, sale, intentType);

            return { email, phone };
        }

        const cached = getCached();

        if (cached) {
            await postLead(cached, sale, intentType);

            return cached;
        }

        const guestContacts = await leadModal.open({ sale, intentType });
        if (guestContacts) setCached(guestContacts);

        return guestContacts;
    };

    return {
        captureLeadContacts,
        closeLeadModal: () => leadModal.close(),
    };
};

export const useSdpLead = createSharedComposable(_useSdpLead);
